import type { IntlShape } from 'react-intl';
import { useIntl, defineMessages } from 'react-intl';
import { DateField } from '@packages/shared/src/components/DateField/DateField';
import type { CustomerDataWithStatus } from '@packages/utilities';
import { useConfig, useCookies, useCustomer } from '@packages/utilities';
import type { ObjectSchema, SchemaDescription } from 'yup';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  CheckboxWithLabel,
  Grid,
  TextField,
  ToggleButtonGroup,
  useI18n,
} from '@packages/shared';
import { useState, type ReactNode } from 'react';
import config from '@packages/config';
import { formMessages, salutationSelectMessages, specialErrorMessages } from '../messages';
import { formatDate } from '../../../utils';
import { noSpecChars, noSpecCharsNoNumbers, numbersOnly } from '../helpers/validationSchema';
import { Errors, InputIDs } from '../types';
import { SuccessPage } from '../SuccessPage';
import type { ConvertedFormMessageDataType } from '../GeneralForm/helpers';
import { submitMessage } from '../GeneralForm/helpers';
import { GeneralFormWrapper } from '../GeneralForm';
import { useFormDeviceType } from '../GeneralForm/helpers/useFormDeviceType';
import { useSendRsvMessageGqlMutation } from './helpers/useSendRsvMessage';

const rateProtectionMessages = defineMessages({
  submit: {
    id: 'forms.rateProtection.submit',
    defaultMessage: 'Jetzt kostenpflichtig bestellen',
  },
  submitDisabled: {
    id: 'forms.rateProtection.submit.disabled',
    defaultMessage: 'Bitte fülle zuerst das Formular aus',
  },
  sucessHeadline: {
    id: 'forms.rateProtection.success.headline',
    defaultMessage: 'Vielen Dank!',
  },
  sucessDescription: {
    id: 'forms.rateProtection.success.description',
    defaultMessage:
      'Deine Angaben wurden erfolgreich übermittelt. Wir bearbeiten deine Anfrage schnellstmöglich und senden dir in Kürze deine Unterlagen zu.',
  },
  messageFieldLabel: {
    id: 'forms.rateProtection.messageFieldLabel',
    defaultMessage: 'Bemerkung',
  },
});

/* Reference of Post Body:
  ------------------------------------------------------
  {
    "subject": "my 'Subject for this email",
    "message": "My very important message, I have to say",
    "customerData": {
      "firstName": "MyFirstname",
      "lastName": "MyLastName",
      "customerId": "anIdMaybeNumbers",
      "street": "MyStreet",
      "housenumber": "2B",
      "city": "My-City",
      "postalCode": "12345",
      "emailAddress": "mymail@example.com",
      "phoneNumber": "01012345",
      "salutation": "female",
      "dateOfBirth": "1980-03-20"
    }
    "messageType": "RSV"
  }
  */

export const convertFormDataToPostData = (formValues: FormValues): ConvertedFormMessageDataType => {
  const salutation = formValues[InputIDs.SALUTATION];
  const dateOfBirth = formValues[InputIDs.BIRTHDAY];

  return {
    subject: '',
    message: formValues[InputIDs.MESSAGE] || '',
    customerData: {
      firstName: formValues[InputIDs.FIRSTNAME] || '',
      lastName: formValues[InputIDs.LASTNAME] || '',
      customerId: formValues[InputIDs.CUSTOMERID] || '',
      street: formValues[InputIDs.STREET] || '',
      housenumber: formValues[InputIDs.HOUSENUMBER] || '',
      city: formValues[InputIDs.CITY] || '',
      postalCode: formValues[InputIDs.POSTALCODE] || '',
      emailAddress: formValues[InputIDs.EMAIL] || '',
      phoneNumber: formValues[InputIDs.PHONENUMBER] || '',
      ...(dateOfBirth && {
        dateOfBirth: formatDate(dateOfBirth),
      }),
      ...(salutation &&
        salutation !== 'notSpecified' && {
          salutation,
        }),
    },
    messageType: 'RSV' as const,
  };
};

enum CheckboxIDs {
  TERMS = 'terms',
}

type CheckboxID = 'terms';
type InputId =
  | 'message'
  | 'firstname'
  | 'lastname'
  | 'street'
  | 'housenumber'
  | 'city'
  | 'postalCode'
  | 'email'
  | 'phoneNumber'
  | 'customerid'
  | 'salutation'
  | 'birthday';
export type FormValues = {
  [InputIDs.MESSAGE]?: string;
  [InputIDs.FIRSTNAME]: string;
  [InputIDs.LASTNAME]: string;
  [InputIDs.STREET]: string;
  [InputIDs.HOUSENUMBER]: string;
  [InputIDs.CITY]: string;
  [InputIDs.POSTALCODE]: string;
  [InputIDs.EMAIL]: string;
  [InputIDs.PHONENUMBER]?: string;
  [InputIDs.CUSTOMERID]?: string;
  [CheckboxIDs.TERMS]: boolean;
  [InputIDs.SALUTATION]?: string;
  [InputIDs.BIRTHDAY]?: string | Date;
};

const buildYupSchemaWithIntlFormatMessage = (
  formatMessage: IntlShape['formatMessage'],
): ObjectSchema<FormValues> =>
  yup.object().shape({
    [InputIDs.CUSTOMERID]: yup
      .string()
      .matches(
        config.forms.validation.rules.customerId.regex,
        formatMessage(config.forms.validation.rules.customerId.errorMessage),
      )
      .required(formatMessage(formMessages[InputIDs.CUSTOMERID].error)),
    [InputIDs.FIRSTNAME]: yup
      .string()
      .matches(noSpecCharsNoNumbers, formatMessage(specialErrorMessages.noSpecCharsNoNumbers))
      .required(formatMessage(formMessages[InputIDs.FIRSTNAME].error)),
    [InputIDs.LASTNAME]: yup
      .string()
      .matches(noSpecCharsNoNumbers, formatMessage(specialErrorMessages.noSpecCharsNoNumbers))
      .required(formatMessage(formMessages[InputIDs.LASTNAME].error)),
    [InputIDs.STREET]: yup
      .string()
      .matches(noSpecCharsNoNumbers, formatMessage(specialErrorMessages.noSpecCharsNoNumbers))
      .required(formatMessage(formMessages[InputIDs.STREET].error)),
    [InputIDs.HOUSENUMBER]: yup
      .string()
      .matches(noSpecChars, formatMessage(specialErrorMessages.noSpecChars))
      .required(formatMessage(formMessages[InputIDs.HOUSENUMBER].error)),
    [InputIDs.POSTALCODE]: yup
      .string()
      .matches(numbersOnly, formatMessage(specialErrorMessages.numbersOnly))
      .required(formatMessage(formMessages[InputIDs.POSTALCODE].error)),
    [InputIDs.CITY]: yup
      .string()
      .matches(noSpecCharsNoNumbers, formatMessage(specialErrorMessages.noSpecCharsNoNumbers))
      .required(formatMessage(formMessages[InputIDs.CITY].error)),
    [InputIDs.EMAIL]: yup
      .string()
      .email(formatMessage(specialErrorMessages.email))
      .required(formatMessage(formMessages[InputIDs.EMAIL].error)),
    [InputIDs.PHONENUMBER]: yup.string(),
    [CheckboxIDs.TERMS]: yup.boolean().required().isTrue(),
    [InputIDs.MESSAGE]: yup.string(),
    [InputIDs.SALUTATION]: yup.string(),
    [InputIDs.BIRTHDAY]: yup.date().typeError(Errors.INVALIDDATE),
  });
const genderSalutationMap = {
  m: 'male',
  f: 'female',
  d: 'notSpecified',
};
const birthdayToDate = (birthday: string) => {
  try {
    const [day, month, year] = birthday.split('.');

    // convert to Date to make sure the string was valid
    // get string for input
    return new Date(`${year}-${month}-${day}`);
  } catch {
    /* istanbul ignore next */
    return undefined;
  }
};
const getDefaultValues = (customer: CustomerDataWithStatus | undefined): FormValues => ({
  [`${InputIDs.FIRSTNAME}`]: customer?.firstName || '',
  [`${InputIDs.LASTNAME}`]: customer?.lastName || '',
  [`${InputIDs.STREET}`]: customer?.billingAddress?.street || '',
  [`${InputIDs.HOUSENUMBER}`]: customer?.billingAddress?.houseNumber || '',
  [`${InputIDs.CITY}`]: customer?.billingAddress?.city || '',
  [`${InputIDs.POSTALCODE}`]: customer?.billingAddress?.zipCode || '',
  [`${InputIDs.EMAIL}`]: customer?.email || '',
  [`${InputIDs.CUSTOMERID}`]: customer?.businessPartnerNo,
  // "find" first element in array. Can't use phoneNumbers[0, as array might be undefined
  [`${InputIDs.PHONENUMBER}`]: customer?.phoneNumbers?.find(Boolean)?.number,
  [CheckboxIDs.TERMS]: false,
  [`${InputIDs.SALUTATION}`]: customer?.gender && genderSalutationMap[customer.gender],
  [`${InputIDs.BIRTHDAY}`]: customer?.birthDay && birthdayToDate(customer.birthDay),
});
type SetErrorType = `root.${InputId}` | 'root';

const RateProtectionForm = () => {
  const { formatMessage } = useIntl();
  const { customer } = useCustomer();
  const { language } = useI18n();
  const schema = buildYupSchemaWithIntlFormatMessage(formatMessage);
  const { control, register, handleSubmit, formState, setError } = useForm<FormValues>({
    resolver: yupResolver(schema),
    mode: 'onTouched',
    defaultValues: getDefaultValues(customer),
  });
  const [errorNotification, setErrorNotification] = useState({ show: false, message: '' });
  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
  const { isSubmitting, isValid, errors } = formState;
  const customMessageField: { placeholder: string; label: string } | undefined = {
    label: formatMessage(rateProtectionMessages.messageFieldLabel),
    placeholder: '',
  };
  const successPage = {
    headline: formatMessage(rateProtectionMessages.sucessHeadline),
    description: formatMessage(rateProtectionMessages.sucessDescription),
  };

  const formDeviceType = useFormDeviceType();
  const { getCookies } = useCookies();
  const {
    forms: { useOptimizely },
  } = useConfig();
  const doSendRsvMessageGql = useSendRsvMessageGqlMutation();
  const onSubmit = async (data: FormValues) => {
    getCookies()['inspire.optimizely'] === 'true' || useOptimizely
      ? doSendRsvMessageGql({ formData: data })
          .then(({ data: resultData, error: resultError }) => {
            if (resultError) {
              throw new Error(resultError.message);
            }
            if (
              resultData?.sendRsvMessage &&
              'errors' in resultData.sendRsvMessage &&
              resultData.sendRsvMessage.errors &&
              resultData.sendRsvMessage.errors.length > 0
            ) {
              throw new Error(
                resultData.sendRsvMessage.errors
                  .map((error) => {
                    if (error) {
                      if (!(error.includes('{') && error.includes('}'))) {
                        return error;
                      }
                      const cleanLinebreaks = error.replace(/\n|\r/g, '');
                      const jsonPart = cleanLinebreaks.substring(
                        cleanLinebreaks.indexOf('{'),
                        cleanLinebreaks.lastIndexOf('}') + 1,
                      );
                      return JSON.parse(jsonPart).errorMessage;
                    }
                    return null;
                  })
                  .join(),
              );
            }
            setIsSubmitSuccessful(true);
          })
          .catch((error) => {
            setErrorNotification({ show: true, message: error.message });
          })
      : await submitMessage<SetErrorType>({
          formatMessage,
          language,
          formDeviceType,
          convertedFormMessageData: convertFormDataToPostData(data),
          setError,
          endpoint: 'message',
          onError: (error) => {
            setErrorNotification({ show: true, message: error });
          },
          onSuccess: () => {
            setIsSubmitSuccessful(true);
          },
        });
  };
  const submitButton = {
    enabled: formatMessage(rateProtectionMessages.submit),
    disabled: formatMessage(rateProtectionMessages.submitDisabled),
  };
  const getFieldProps = (inputId: InputId) => ({
    label: formatMessage(formMessages[inputId].label),
    ...register(inputId),
    required: !(schema.describe().fields[inputId] as SchemaDescription).optional,
    helperText: errors[inputId]?.message, // is provided by the yup schema
    error: !!errors[inputId],
    placeholder:
      formMessages[inputId].placeholder.defaultMessage !== ''
        ? formatMessage(formMessages[inputId].placeholder)
        : undefined,
    fullWidth: true,
  });
  const getCheckboxProps = (checkboxId: CheckboxID) => ({
    checkboxProps: {
      id: checkboxId,
    },
    ...register(checkboxId),
    label: formatMessage(formMessages[checkboxId]?.label),
    sx: {
      marginY: 1,
      ...(errors[checkboxId] && /* istanbul ignore next  */ { color: 'error.main' }),
    },
  });
  if (isSubmitSuccessful) {
    return <SuccessPage {...successPage} />;
  }
  return (
    <GeneralFormWrapper
      isSubmitting={isSubmitting}
      isValid={isValid}
      submitButton={submitButton}
      submitHandler={handleSubmit(onSubmit)}
      handleOnClose={() => setErrorNotification({ ...errorNotification, show: false })}
      errorNotification={errorNotification}
    >
      <Grid container columns={[1, 1, 2]} sx={{ marginBottom: 3 }} columnSpacing={4} rowSpacing={3}>
        <Grid item xs={1} md={1}>
          <TextField {...getFieldProps(`${InputIDs.CUSTOMERID}`)} />
        </Grid>

        <Grid item xs={1} md={1}>
          <Controller
            control={control}
            name="salutation"
            render={({ field: { onChange, value } }) => (
              <ToggleButtonGroup
                id={InputIDs.SALUTATION}
                label={formatMessage(formMessages[InputIDs.SALUTATION].label)}
                options={(['notSpecified', 'female', 'male'] as const).map((optionValue) => ({
                  value: optionValue,
                  label: formatMessage(salutationSelectMessages[optionValue]),
                }))}
                value={value}
                handleChange={onChange}
              />
            )}
          />
        </Grid>

        <Grid item xs={1}>
          <TextField {...getFieldProps(`${InputIDs.FIRSTNAME}`)} />
        </Grid>
        <Grid item xs={1}>
          <TextField {...getFieldProps(`${InputIDs.LASTNAME}`)} />
        </Grid>
        <Grid item xs={1}>
          <Grid container columnSpacing={1.5} rowSpacing={1.5}>
            <Grid item xs>
              <TextField {...getFieldProps(`${InputIDs.STREET}`)} />
            </Grid>
            <Grid item sx={{ width: '7rem' }}>
              <TextField {...getFieldProps(`${InputIDs.HOUSENUMBER}`)} />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={1}>
          <Grid container columnSpacing={1.5} rowSpacing={1.5}>
            <Grid item sx={{ width: '9rem' }}>
              <TextField {...getFieldProps(`${InputIDs.POSTALCODE}`)} type="tel" />
            </Grid>
            <Grid item xs>
              <TextField {...getFieldProps(`${InputIDs.CITY}`)} />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={1}>
          <TextField {...getFieldProps(`${InputIDs.EMAIL}`)} />
        </Grid>
        <Grid item xs={1}>
          <Controller
            name="birthday"
            control={control}
            render={({ field: { onChange } }) => (
              <DateField {...getFieldProps(InputIDs.BIRTHDAY)} onChange={onChange} />
            )}
          />
        </Grid>
      </Grid>
      <TextField
        {...getFieldProps(InputIDs.MESSAGE)}
        {...(customMessageField && {
          placeholder: customMessageField.placeholder,
          label: customMessageField.label,
        })}
        type="textarea"
        multiline
        minRows={4}
        maxRows={8}
      />
      <Box
        sx={{
          paddingLeft: 1,
          marginY: 1,
          '& span': { typography: 'body2' },
          '& p': {
            m: 0,
            pt: 0,
            pb: 1,
            '&:last-of-type': {
              pb: 0,
            },
          },
          '& a': {
            color: 'text.dark',
            textDecoration: 'underline',
          },
        }}
      >
        <CheckboxWithLabel
          {...getCheckboxProps(CheckboxIDs.TERMS)}
          label={formatMessage(formMessages[CheckboxIDs.TERMS].label, {
            p: (msg: ReactNode) => <p>{msg}</p>,
            avb: (msg: ReactNode) => (
              <a href={formatMessage(formMessages[CheckboxIDs.TERMS].linkAvb)}>{msg}</a>
            ),
            ipid: (msg: ReactNode) => (
              <a href={formatMessage(formMessages[CheckboxIDs.TERMS].linkIpid)}>{msg}</a>
            ),
            vag: (msg: ReactNode) => (
              <a href={formatMessage(formMessages[CheckboxIDs.TERMS].linkVag)}>{msg}</a>
            ),
            vvinfo: (msg: ReactNode) => (
              <a href={formatMessage(formMessages[CheckboxIDs.TERMS].linkVvinfo)}>{msg}</a>
            ),
          })}
        />
      </Box>
    </GeneralFormWrapper>
  );
};

// eslint-disable-next-line import/no-default-export
export default RateProtectionForm;
